import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';
import {
  buildCountryTitleListParams,
  normalizeCountryTitleStatus,
  toCountryTitleApiPayload,
} from '../../../utils/country-title-catalog.utils';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Motivos por país.
 * Endpoint: `/settings/countries/{countryId}/reasons`
 */
@Injectable({ providedIn: 'root' })
export class ReasonService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private resource(countryId: string, id?: string | number): string {
    const baseUrl = `${this.apiUrl}/${countryId}/reasons`;
    return id !== undefined && id !== null ? `${baseUrl}/${id}` : baseUrl;
  }

  private mapItem(it: any): Reason {
    return {
      id: it.id,
      prefix: it.code || it.prefix || '',
      name: it.name || it.title || '',
      detail: it.detail ?? false,
      status: normalizeCountryTitleStatus(it.status),
    };
  }

  getReasons(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<ReasonPageResponse> {
    const params = buildCountryTitleListParams({
      page,
      size,
      sort,
      direction,
      search,
      status,
    });
    return this.httpClient
      .get<ApiResponse<PageableData>>(this.resource(countryId), { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((it: any) => this.mapItem(it)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          } as ReasonPageResponse;
        }),
      );
  }

  getReasonById(countryId: string, id: string | number) {
    return this.httpClient
      .get<ApiResponse<any>>(this.resource(countryId, id))
      .pipe(map((resp) => this.mapItem(resp.data)));
  }

  createReason(countryId: string, payload: any) {
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        this.resource(countryId),
        toCountryTitleApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Motivo creado' })));
  }

  updateReason(countryId: string, id: string | number, payload: any) {
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        this.resource(countryId, id),
        toCountryTitleApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Motivo actualizado' })));
  }

  deleteReason(countryId: string, id: string | number) {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(
        this.resource(countryId, id),
      )
      .pipe(map((res) => ({ message: res.message || 'Motivo eliminado' })));
  }
}

export interface Reason {
  id: string;
  prefix: string;
  name: string;
  status?: string;
  detail?: boolean;
}

export interface ReasonPageResponse {
  items: Reason[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

